import React from "react";
import { Link } from "react-router-dom";
import { Robot } from "@phosphor-icons/react";

const sections = {
  privacy: {
    title: "Privacy Policy",
    updated: "January 27, 2026",
    body: [
      ["What we collect",
       "When you sign up, we collect your name, email address, and (if you choose JWT auth) a hash of your password. If you sign in with Google, we receive your name, email, and profile picture from Google. When you use the product, we store the emails and lead data you paste or upload, your bot's training data (FAQs and tone), and aggregate usage counters."],
      ["How we use it",
       "We use your data only to operate the Robocope service: to authenticate you, run the AI automations on your behalf (email triage, lead scoring, customer chat), and provide aggregate workspace statistics. We do not sell your data. We do not use your email or lead content to train any AI models."],
      ["Where it lives",
       "Your data is stored in MongoDB on infrastructure managed by Emergent. AI calls are routed to Anthropic (Claude) via a hosted gateway. We do not retain copies of AI prompts/responses outside your workspace."],
      ["Cookies",
       "We set httpOnly authentication cookies (\"access_token\", \"refresh_token\", or \"session_token\") to keep you signed in. We do not use third-party advertising cookies."],
      ["Your rights",
       "You can request export or deletion of your workspace data at any time by emailing privacy@robocope.com. Account deletion is irreversible and removes all associated emails, leads, chat logs, and bot configuration."],
      ["Contact",
       "Questions about this policy? Email privacy@robocope.com."],
    ],
  },
  terms: {
    title: "Terms of Service",
    updated: "January 27, 2026",
    body: [
      ["The deal",
       "Robocope is a SaaS tool that helps small businesses automate repetitive workload via AI. By using the service you agree to these terms."],
      ["Acceptable use",
       "Don't use Robocope to send spam, phishing, harassment, or content that violates the law or third-party rights. Don't try to break, scrape, or reverse-engineer the service. Don't share your login with people outside your team."],
      ["Account",
       "You are responsible for everything that happens under your account. Keep your password safe. Notify us immediately of any unauthorized access."],
      ["Plans & billing",
       "Paid plans renew monthly or annually as selected. You can cancel any time — your plan stays active until the end of the paid period. We don't offer prorated refunds for partial months. Volume limits apply per plan tier."],
      ["AI outputs",
       "AI-generated drafts are suggestions, not legal or professional advice. You are responsible for reviewing and approving any reply or action before it leaves your workspace."],
      ["Service availability",
       "We aim for 99.9% uptime but make no guarantees. The service is provided \"as is\" without warranty."],
      ["Liability",
       "To the maximum extent permitted by law, Robocope's total liability is limited to the amount you paid us in the previous 12 months."],
      ["Changes",
       "We may update these terms occasionally. Continued use after a change means you accept the new version."],
      ["Contact",
       "Questions? Email hello@robocope.com."],
    ],
  },
};

export default function LegalPage({ kind }) {
  const data = sections[kind] || sections.privacy;
  return (
    <div className="bg-white">
      <header className="border-b border-[var(--line)]">
        <div className="max-w-[900px] mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[var(--ink)] flex items-center justify-center">
              <Robot size={22} weight="duotone" color="#FFD600" />
            </div>
            <span className="font-display text-2xl tracking-tight">Robocope</span>
          </Link>
          <Link to="/" className="text-sm hover:underline">← Home</Link>
        </div>
      </header>
      <article className="max-w-[820px] mx-auto px-8 py-16" data-testid={`legal-${kind}`}>
        <div className="overline text-[var(--ink-soft)]">/ Legal · last updated {data.updated}</div>
        <h1 className="font-display text-5xl tracking-tighter mt-3 mb-10">{data.title}</h1>
        <div className="space-y-8">
          {data.body.map(([h, p]) => (
            <section key={h}>
              <h2 className="font-display text-2xl tracking-tight mb-2">{h}</h2>
              <p className="text-[var(--ink-soft)] leading-relaxed">{p}</p>
            </section>
          ))}
        </div>
        <div className="mt-12 cell p-4 bg-[var(--bg-soft)] text-xs text-[var(--ink-soft)]">
          Plain-English summary. This document is provided as a starting point — for production use, please have your legal counsel review and customize for your jurisdiction.
        </div>
      </article>
      <footer className="bg-[var(--ink)] text-white py-8 mt-16">
        <div className="max-w-[1320px] mx-auto px-8 flex flex-wrap items-center justify-between gap-3 text-sm">
          <span className="text-white/50">© 2026 Robocope. All rights reserved.</span>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white/80">Privacy</Link>
            <Link to="/terms" className="hover:text-white/80">Terms</Link>
            <Link to="/pricing" className="hover:text-white/80">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
